import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Palette, Users, ChevronLeft, ChevronRight, Compass, Newspaper, Sparkles } from 'lucide-react';
import PosterSection from '@/components/home/PosterSection';
import FeaturedArtworks from '@/components/home/FeaturedArtworks';
import SilkRoadExplorer from '@/components/home/SilkRoadExplorer';
import LatestPosts from '@/components/home/LatestPosts';
import AboutSection from '@/components/home/AboutSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [activeCard, setActiveCard] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [aboutUs, setAboutUs] = useState(null);
  const { t } = useTranslation();
  const autoPlayRef = useRef(null);

  // 获取关于我们数据
  useEffect(() => {
    axios.get(`${API}/settings/about`)
      .then(res => setAboutUs(res.data))
      .catch(err => console.error('Failed to fetch about us:', err));
  }, []);

  // 三个主要入口卡片 - 使用翻译键
  const cards = [
    {
      id: 'artcanvas',
      titleKey: 'heroSlider.artCanvas.title',
      subtitleKey: 'heroSlider.artCanvas.badge',
      descriptionKey: 'heroSlider.artCanvas.subtitle',
      detailKey: 'heroSlider.artCanvas.roles',
      ctaKey: 'heroSlider.artCanvas.cta',
      icon: Palette,
      path: '/artcanvas',
      gradient: 'from-gray-900 via-gray-800 to-black',
      accent: 'text-white',
      bgImage: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=1200&q=80',
    },
    {
      id: 'silkroad',
      titleKey: 'heroSlider.silkRoad.title',
      subtitleKey: 'heroSlider.silkRoad.badge',
      descriptionKey: 'heroSlider.silkRoad.subtitle',
      detailKey: 'heroSlider.silkRoad.roles',
      ctaKey: 'heroSlider.silkRoad.cta',
      icon: Compass,
      path: '/explore',
      gradient: 'from-amber-950 via-amber-900 to-stone-950',
      accent: 'text-amber-200',
      bgImage: 'https://images.pexels.com/photos/12895031/pexels-photo-12895031.jpeg?auto=compress&cs=tinysrgb&w=1200',
    },
    {
      id: 'community',
      titleKey: 'heroSlider.community.title',
      subtitleKey: 'heroSlider.community.badge',
      descriptionKey: 'heroSlider.community.subtitle',
      detailKey: 'heroSlider.community.roles',
      ctaKey: 'heroSlider.community.cta',
      icon: Users,
      path: '/community',
      gradient: 'from-teal-900 via-emerald-800 to-green-950',
      accent: 'text-emerald-100',
      bgImage: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1200&q=80',
    },
    {
      id: 'latestposts',
      titleKey: 'heroSlider.latestPosts.title',
      subtitleKey: 'heroSlider.latestPosts.badge',
      descriptionKey: 'heroSlider.latestPosts.subtitle',
      detailKey: 'heroSlider.latestPosts.roles',
      ctaKey: 'heroSlider.latestPosts.cta',
      icon: Newspaper,
      path: '/#latest-posts',
      gradient: 'from-indigo-950 via-blue-900 to-slate-950',
      accent: 'text-blue-200',
      bgImage: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&q=80',
    },
    {
      id: 'exhibitions',
      titleKey: 'heroSlider.exhibitions.title',
      subtitleKey: 'heroSlider.exhibitions.badge',
      descriptionKey: 'heroSlider.exhibitions.subtitle',
      detailKey: 'heroSlider.exhibitions.roles',
      ctaKey: 'heroSlider.exhibitions.cta',
      icon: Sparkles,
      path: '/exhibitions',
      gradient: 'from-rose-950 via-rose-900 to-pink-950',
      accent: 'text-rose-200',
      bgImage: 'https://images.unsplash.com/photo-1769321013386-25830e12a2af?w=1200&q=80',
    }
  ];

  // 自动轮播
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(() => {
        setActiveCard((prev) => (prev + 1) % cards.length);
      }, 5000);
    }
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, cards.length]);

  // 切换卡片 - 添加事件阻止冒泡
  const handleCardChange = (index, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setActiveCard(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    e.preventDefault();
    handleCardChange((activeCard - 1 + cards.length) % cards.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    e.preventDefault();
    handleCardChange((activeCard + 1) % cards.length);
  };

  return (
    <div className="min-h-screen bg-amber-50 pt-16" data-testid="homepage">
      {/* 全屏卡片展示区 */}
      <section className="relative h-[calc(100vh-48px)] overflow-hidden">
        {/* 背景图片层 */}
        {cards.map((card, index) => (
          <div
            key={card.id}
            className={`absolute inset-0 transition-all duration-1000 ease-out ${
              index === activeCard 
                ? 'opacity-100 scale-100' 
                : 'opacity-0 scale-105'
            }`}
          >
            {/* 背景图 */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${card.bgImage})` }}
            />
            {/* 渐变遮罩 */}
            <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-85`} />
          </div>
        ))}

        {/* 内容区 - 使用Link进行导航（Safari兼容） */}
        <Link 
          to={cards[activeCard].path}
          onClick={(e) => {
            const path = cards[activeCard].path;
            if (path.startsWith('/#')) {
              e.preventDefault();
              const el = document.getElementById(path.substring(2));
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          className="relative z-10 h-full flex flex-col justify-center items-center px-6 cursor-pointer block"
          data-testid="carousel-content"
        >
          {/* 卡片内容 */}
          <div className="text-center max-w-4xl mx-auto">
            {cards.map((card, index) => (
              <div
                key={card.id}
                className={`transition-all duration-700 ${
                  index === activeCard 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8 absolute pointer-events-none'
                }`}
                style={{ display: index === activeCard ? 'block' : 'none' }}
              >
                {/* 图标 */}
                <div className="mb-8 flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <card.icon className="w-10 h-10 text-white" />
                  </div>
                </div>

                {/* 副标题 */}
                <p className={`text-lg md:text-xl ${card.accent} mb-4 tracking-widest uppercase`}>
                  {t(card.subtitleKey)}
                </p>

                {/* 主标题 */}
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-light text-white mb-6 tracking-tight">
                  {t(card.titleKey)}
                </h1>

                {/* 描述 */}
                <p className="text-xl md:text-2xl text-white/80 font-light mb-4 max-w-2xl mx-auto">
                  {t(card.descriptionKey)}
                </p>

                {/* 细节 */}
                <p className="text-sm md:text-base text-white/50 tracking-wide">
                  {t(card.detailKey)}
                </p>

                {/* 点击提示 - 仅在桌面端显示 */}
                <p className="hidden md:block text-xs text-white/40 mt-8 tracking-wider">
                  {t(card.ctaKey)}
                </p>
              </div>
            ))}
          </div>

          {/* 底部导航指示器 */}
          <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-6 md:gap-8 z-20">
            {/* 左箭头 */}
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white/70 hover:text-white hover:border-white/60 transition-all"
              data-testid="carousel-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* 指示点 */}
            <div className="flex gap-3">
              {cards.map((card, index) => (
                <button
                  key={card.id}
                  onClick={(e) => handleCardChange(index, e)}
                  className={`transition-all duration-300 ${
                    index === activeCard 
                      ? 'w-8 h-2 bg-white rounded-full' 
                      : 'w-2 h-2 bg-white/40 hover:bg-white/60 rounded-full'
                  }`}
                  data-testid={`carousel-dot-${index}`}
                />
              ))}
            </div>

            {/* 右箭头 */}
            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white/70 hover:text-white hover:border-white/60 transition-all"
              data-testid="carousel-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

        </Link>

        {/* 滚动提示 - 仅移动端显示 */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce opacity-50 lg:hidden">
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Featured Artworks */}
      <FeaturedArtworks />

      {/* Silk Road Art & Culture Explorer */}
      <SilkRoadExplorer />

      {/* Latest Posts */}
      <LatestPosts />

      {/* 海报板块 */}
      <PosterSection />

      {/* 关于我们 */}
      {aboutUs && <AboutSection aboutUs={aboutUs} />}
    </div>
  );
};

export default HomePage;
